import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface DateFilterConfig {
  type: 'ultimo' | 'siguiente' | 'este';
  number: number;
  period: 'dias' | 'semana' | 'semana-calendario' | 'mes' | 'mes-calendario' | 'anho' | 'anho-calendario';
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[];
  selected: string | string[] | null;
  loading: boolean;
  loadFn: () => Observable<FilterOption[]>;
  defaultValue?: (options: FilterOption[]) => string | string[];
  reload?: boolean;
}

export interface ActiveFilter {
  filterType: string;
  label: string;
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  // Date filter properties
  public startDate: Date | null = null;
  public endDate: Date | null = null;
  public dateFilterConfig: DateFilterConfig = {
    type: 'ultimo',
    number: 3,
    period: 'mes'
  };

  // Filter options
  public companyOptions: FilterOption[] = [];
  public userOptions: FilterOption[] = [];
  public clientOptions: FilterOption[] = [];

  // Active filters map
  private activeFiltersMap = new Map<string, ActiveFilter>();
  private activeFiltersSubject = new BehaviorSubject<ActiveFilter[]>([]);
  public activeFilters$ = this.activeFiltersSubject.asObservable();

  // Subjects for reactive updates
  private dateChangeSubject = new BehaviorSubject<{ startDate: Date | null, endDate: Date | null }>({
    startDate: null,
    endDate: null
  });
  private filterChangeSubject = new BehaviorSubject<any>({});

  public dateChange$ = this.dateChangeSubject.asObservable();
  public filterChange$ = this.filterChangeSubject.asObservable();

  constructor() {
    this.calculateInitialDates();
  }

  /**
   * Calculate initial dates based on default configuration
   */
  private calculateInitialDates(): void {
    this.calculateDates();
  }

  /**
   * Calculate dates based on current configuration
   */
  public calculateDates(): void {
    let startDate: Date;
    let endDate: Date;

    if (this.dateFilterConfig.type === 'este') {
      // Para "Este", calculamos el rango completo del período actual
      const dates = this.calculateCurrentPeriod(this.dateFilterConfig.period);
      startDate = dates.start;
      endDate = dates.end;
    } else {
      // Para "Último" y "Siguiente"
      const multiplier = this.dateFilterConfig.type === 'ultimo' ? -1 : 1;
      const dates = this.calculateRelativePeriod(
        this.dateFilterConfig.period,
        this.dateFilterConfig.number * multiplier
      );
      startDate = dates.start;
      endDate = dates.end;
    }

    this.startDate = startDate;
    this.endDate = endDate;
    this.dateChangeSubject.next({ startDate, endDate });
  }

  /**
   * Update date filter configuration
   */
  public updateDateFilter(config: Partial<DateFilterConfig>): void {
    this.dateFilterConfig = { ...this.dateFilterConfig, ...config };

    // Si se selecciona "Este" y el período actual es de calendario, cambiar a la versión sin calendario
    if (
      this.dateFilterConfig.type === 'este' &&
      this.dateFilterConfig.period.includes('calendario')
    ) {
      const calendarToNonCalendar: { [key: string]: string } = {
        'semana-calendario': 'semana',
        'mes-calendario': 'mes',
        'anho-calendario': 'anho',
      };

      const newPeriod = calendarToNonCalendar[this.dateFilterConfig.period];
      if (newPeriod) {
        this.dateFilterConfig.period = newPeriod as any;
      }
    }

    this.calculateDates();
  }

  /**
   * Update other filters
   */
  public updateFilter(key: string, value: any): void {
    const currentFilters = this.filterChangeSubject.value;
    this.filterChangeSubject.next({ ...currentFilters, [key]: value });
  }

  /**
   * Update multiple filters atomically to avoid race conditions
   */
  public updateFilters(updates: { [key: string]: any }): void {
    const currentFilters = this.filterChangeSubject.value;
    this.filterChangeSubject.next({ ...currentFilters, ...updates });
  }

  /**
   * Update dates and emit dateChange$ observable
   */
  public updateDates(startDate: Date | null, endDate: Date | null): void {
    this.startDate = startDate;
    this.endDate = endDate;
    this.dateChangeSubject.next({ startDate, endDate });
  }

  /**
   * Get current filter values
   */
  public getCurrentFilters(): any {
    return {
      dateFrom: this.startDate?.toISOString(),
      dateTo: this.endDate?.toISOString(),
      ...this.filterChangeSubject.value
    };
  }

  /**
   * Calculate current period dates
   */
  private calculateCurrentPeriod(period: string): { start: Date; end: Date } {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    switch (period) {
      case 'semana':
        // Esta semana: desde el lunes de esta semana hasta el domingo
        const dayOfWeek = now.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Lunes = día 1, Domingo = día 0
        start.setDate(now.getDate() + mondayOffset);
        end.setDate(start.getDate() + 6); // Domingo de la misma semana
        break;

      case 'mes':
        // Este mes: primer día del mes actual hasta el último día del mes actual
        start.setDate(1);
        end.setMonth(now.getMonth() + 1, 0); // Último día del mes actual
        break;

      case 'anho':
        // Este año: 1 de enero hasta 31 de diciembre del año actual
        start.setMonth(0, 1); // 1 de enero
        end.setMonth(11, 31); // 31 de diciembre
        break;
    }

    return { start, end };
  }

  /**
   * Calculate relative period dates
   */
  private calculateRelativePeriod(
    period: string,
    amount: number
  ): { start: Date; end: Date } {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    switch (period) {
      case 'dias':
        if (amount < 0) {
          // Últimos N días: desde N días atrás hasta ayer
          start.setDate(now.getDate() + amount);
        } else {
          // Siguientes N días: desde mañana hasta N días después
          end.setDate(now.getDate() + amount);
        }
        break;

      case 'semana':
        if (amount < 0) {
          // Últimas N semanas: N*7 días atrás hasta hace 7 días
          start.setDate(now.getDate() + amount * 7);
        } else {
          // Siguientes N semanas: desde mañana hasta N*7 días después
          end.setDate(now.getDate() + amount * 7);
        }
        break;

      case 'semana-calendario':
        // Calcular semanas completas de calendario
        const currentWeekStart = new Date(now);
        const dayOfWeek = currentWeekStart.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        currentWeekStart.setDate(currentWeekStart.getDate() + mondayOffset);

        if (amount < 0) {
          // Últimas N semanas: desde inicio de semana N hasta fin de semana anterior
          start.setDate(currentWeekStart.getDate() + amount * 7);
          end.setDate(currentWeekStart.getDate() - 1);
        } else {
          // Siguientes N semanas: desde siguiente semana hasta N semanas después
          start.setDate(currentWeekStart.getDate() + 7);
          end.setDate(currentWeekStart.getDate() + amount * 7 + 6);
        }
        break;

      case 'mes':
        if (amount < 0) {
          // Últimos N meses: N*30 días atrás hasta ayer
          start.setDate(now.getDate() + amount * 30);
        } else {
          // Siguientes N meses: desde mañana hasta N*30 días después
          end.setDate(now.getDate() + amount * 30);
        }
        break;

      case 'mes-calendario':
        if (amount < 0) {
          // Últimos N meses: desde inicio del mes N atrás hasta fin del mes anterior al actual
          start.setMonth(now.getMonth() + amount, 1);
          end.setMonth(now.getMonth(), 0); // Último día del mes anterior al actual
        } else {
          // Siguientes N meses: desde inicio del siguiente mes hasta fin del mes N
          start.setMonth(now.getMonth() + 1, 1);
          end.setMonth(now.getMonth() + amount + 1, 0);
        }
        break;

      case 'anho':
        if (amount < 0) {
          // Últimos N años: N*365 días atrás hasta ayer
          start.setDate(now.getDate() + amount * 365);
        } else {
          // Siguientes N años: desde mañana hasta N*365 días después
          end.setDate(now.getDate() + amount * 365);
        }
        break;

      case 'anho-calendario':
        if (amount < 0) {
          // Últimos N años: desde 1 enero del año N atrás hasta 31 dic del año anterior al actual
          start.setFullYear(now.getFullYear() + amount, 0, 1);
          end.setFullYear(now.getFullYear() - 1, 11, 31);
        } else {
          // Siguientes N años: desde 1 enero del siguiente año hasta 31 dic del año N
          start.setFullYear(now.getFullYear() + 1, 0, 1);
          end.setFullYear(now.getFullYear() + amount, 11, 31);
        }
        break;
    }

    return { start, end };
  }

  /**
   * Get formatted date range string
   */
  public getFormattedDateRange(): string {
    if (!this.startDate || !this.endDate) {
      return 'Seleccione fechas';
    }
    const startFormatted = this.formatDate(this.startDate);
    const endFormatted = this.formatDate(this.endDate);
    return `${startFormatted} - ${endFormatted}`;
  }

  /**
   * Format date to string
   */
  private formatDate(date: Date): string {
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Get date type options
   */
  public getDateTypeOptions(): FilterOption[] {
    return [
      { label: 'Último', value: 'ultimo' },
      { label: 'Siguiente', value: 'siguiente' },
      { label: 'Este', value: 'este' }
    ];
  }

  /**
   * Get period options based on date type
   */
  public getPeriodOptions(): FilterOption[] {
    const allOptions: FilterOption[] = [
      { label: 'Días', value: 'dias' },
      { label: 'Semana', value: 'semana' },
      { label: 'Semana (calendario)', value: 'semana-calendario' },
      { label: 'Mes', value: 'mes' },
      { label: 'Mes (calendario)', value: 'mes-calendario' },
      { label: 'Año', value: 'anho' },
      { label: 'Año (calendario)', value: 'anho-calendario' }
    ];

    if (this.dateFilterConfig.type === 'este') {
      // Filtrar opciones que contienen "calendario" cuando "Este" está seleccionado
      return allOptions.filter(option => !option.value.includes('calendario'));
    }
    return allOptions;
  }

  /**
   * Get number options for date filter
   */
  public getNumberOptions(): FilterOption[] {
    return Array.from({ length: 12 }, (_, i) => ({
      label: String(i + 1),
      value: String(i + 1)
    }));
  }

  /**
   * Initialize active filters based on filter mode
   */
  public initializeActiveFilters(filterMode: string): void {
    this.activeFiltersMap.clear();

    const filterConfigs = this.getFilterConfigsByMode(filterMode);

    filterConfigs.forEach(config => {
      this.activeFiltersMap.set(config.filterType, {
        filterType: config.filterType,
        label: config.label,
        value: ''
      });
    });

    this.emitActiveFilters();
  }

  /**
   * Get filter configurations by mode
   */
  private getFilterConfigsByMode(filterMode: string): { filterType: string; label: string }[] {
    const configs: { [key: string]: { filterType: string; label: string }[] } = {
      'crm': [
        { filterType: 'companies', label: 'Compañía' },
        { filterType: 'users', label: 'Usuario' },
        { filterType: 'clients', label: 'Cliente' }
      ],
      'crm-totals': [
        { filterType: 'companies', label: 'Compañía' },
        { filterType: 'amountMin', label: 'Monto mínimo' },
        { filterType: 'amountMax', label: 'Monto máximo' }
      ],
      'finance': [
        { filterType: 'projects', label: 'Proyecto' },
        { filterType: 'country', label: 'País' },
        { filterType: 'margin', label: 'Margen' },
        { filterType: 'amountMin', label: 'Monto mínimo' },
        { filterType: 'amountMax', label: 'Monto máximo' }
      ],
      'projects': [
        { filterType: 'projects', label: 'Proyecto' },
        { filterType: 'merchandise', label: 'Mercancía' },
        { filterType: 'status', label: 'Estado' },
        { filterType: 'mbl', label: 'MBL' },
        { filterType: 'hbl', label: 'HBL' }
      ],
      'inventory-operations': [
        { filterType: 'origin', label: 'Origen' },
        { filterType: 'destination', label: 'Destino' },
        { filterType: 'supplier', label: 'Proveedor' },
        { filterType: 'product', label: 'Producto' },
        { filterType: 'batch', label: 'Lote' },
        { filterType: 'serialNumber', label: 'Número de serie' }
      ],
      'inventory-stock': [
        { filterType: 'product', label: 'Producto' },
        { filterType: 'location', label: 'Ubicación' }
      ],
      'temperature': [
        { filterType: 'bess', label: 'BESS' },
        { filterType: 'destinationTransportRef', label: 'Motonave' },
        { filterType: 'mbl', label: 'MBL' },
        { filterType: 'projects', label: 'Proyecto' }
      ],
      'carbonFootprint': [
        { filterType: 'projectCarbon', label: 'Proyecto' },
        { filterType: 'shippingRef', label: 'Referencia de envío' },
        { filterType: 'mblCarbon', label: 'MBL' },
        { filterType: 'container', label: 'Contenedor' }
      ],
      'operations': [
        { filterType: 'operationsProject', label: 'Proyecto' },
        { filterType: 'operationsContainer', label: 'Contenedor' },
        { filterType: 'operationsServices', label: 'Servicios' },
        { filterType: 'operationsMBL', label: 'MBL' },
        { filterType: 'operationsHBL', label: 'HBL' }
      ]
    };

    return configs[filterMode] || configs['crm'];
  }

  /**
   * Update active filter value
   */
  public updateActiveFilterValue(filterType: string, valueLabels: string): void {
    const existingFilter = this.activeFiltersMap.get(filterType);

    if (existingFilter) {
      this.activeFiltersMap.set(filterType, {
        ...existingFilter,
        value: valueLabels
      });
      this.emitActiveFilters();
    }
  }

  /**
   * Get active filters as array
   */
  public getActiveFilters(): ActiveFilter[] {
    return Array.from(this.activeFiltersMap.values());
  }

  /**
   * Emit active filters
   */
  private emitActiveFilters(): void {
    this.activeFiltersSubject.next(this.getActiveFilters());
  }
}
